import { useSyncExternalStore } from 'react';
import { useI18n } from './i18n';
import { SelectField } from './ui';

/**
 * Light unless somebody chooses otherwise. The app used to follow the system's setting only, so on
 * a machine set to dark it was dark with no way out; now the system's setting is one choice of
 * three. Remembered in this browser, like the language; applied before the first paint by
 * public/theme.js, which reads the same keys.
 *
 * The contrast (TF-85) works the same way: the system's (prefers-contrast: more gives high), or a
 * level chosen here. Both are attributes on <html> that libs/design-tokens/colors.css reads.
 */
export type Theme = 'light' | 'dark' | 'system';
export const THEMES: Theme[] = ['light', 'dark', 'system'];
export type ContrastChoice = 'system' | 'standard' | 'medium' | 'high';
export const CONTRAST_CHOICES: ContrastChoice[] = ['system', 'standard', 'medium', 'high'];
/** Rows 52 high in tables, or 40 for more on the screen (TF-95). */
export type Density = 'comfortable' | 'compact';
export const DENSITIES: Density[] = ['comfortable', 'compact'];

/** One remembered choice: its localStorage key, the <html> attribute it sets, and its default. */
function preference<T extends string>(key: string, attribute: 'theme' | 'contrast' | 'density', choices: readonly T[], fallback: T) {
  const listeners = new Set<() => void>();
  const read = (): T => {
    try {
      const saved = localStorage.getItem(key);
      return choices.includes(saved as T) ? (saved as T) : fallback;
    } catch {
      return fallback;
    }
  };
  const set = (value: T): void => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Private windows may refuse; the choice then lasts until the page is closed.
    }
    document.documentElement.dataset[attribute] = value;
    listeners.forEach((listener) => listener());
  };
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };
  /** The current choice, shared by every picker on the page. */
  const use = (): [T, (value: T) => void] => [useSyncExternalStore(subscribe, read, () => fallback), set];
  return { set, use };
}

const theme = preference<Theme>('teamfolio.theme', 'theme', THEMES, 'light');
const contrast = preference<ContrastChoice>('teamfolio.contrast', 'contrast', CONTRAST_CHOICES, 'system');
const density = preference<Density>('teamfolio.density', 'density', DENSITIES, 'comfortable');

// Paper is white (TF-115): a dark theme would print a page of ink. The theme is light while the
// browser prints, and what it was afterwards.
if (typeof window !== 'undefined') {
  let before: string | undefined;
  window.addEventListener('beforeprint', () => {
    before = document.documentElement.dataset.theme;
    document.documentElement.dataset.theme = 'light';
  });
  window.addEventListener('afterprint', () => {
    if (before !== undefined) document.documentElement.dataset.theme = before;
    before = undefined;
  });
}

export const setTheme = theme.set;
export const useTheme = theme.use;
export const setContrast = contrast.set;
export const useContrast = contrast.use;
export const useDensity = density.use;

export function ThemePicker({ id = 'theme' }: { id?: string }) {
  const { t } = useI18n();
  const [current, choose] = useTheme();
  return (
    <SelectField
      id={id}
      label={t('nav.appearance')}
      hideLabel
      dense
      className="m-0"
      value={current}
      onChange={(value) => choose(value as Theme)}
      options={THEMES.map((option) => ({ value: option, label: t(`theme.${option}`) }))}
    />
  );
}

export function ContrastPicker({ id = 'contrast' }: { id?: string }) {
  const { t } = useI18n();
  const [current, choose] = useContrast();
  return (
    <SelectField
      id={id}
      label={t('contrast.label')}
      hideLabel
      dense
      className="m-0"
      value={current}
      onChange={(value) => choose(value as ContrastChoice)}
      options={CONTRAST_CHOICES.map((option) => ({ value: option, label: t(`contrast.${option}`) }))}
    />
  );
}
